import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useGetOrCreatePurchaseLoanApplicationMutation } from '@/apis/extended/loanApplicationApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { useProperties } from '@/hooks/useProperties';
import type { PropertyView } from '@/types/PropertyView';

/**
 * One step from "I'm pre-qualified" to the application form: open (or create)
 * the file for the home the borrower is working on, already linked so its
 * address, price or mortgage fill in. Replaces the loan type → My applications
 * → Start detour.
 */
export function useStartLoanApplication() {
  const navigate = useNavigate();
  const { data: user } = useGetCurrentUserQuery();
  const { allProperties } = useProperties();
  const [getOrCreate, { isLoading }] =
    useGetOrCreatePurchaseLoanApplicationMutation();

  /** The home the borrower most likely means, given what they came to do. */
  const defaultProperty = useMemo<PropertyView | undefined>(() => {
    const own = allProperties.filter((p) => p.pipelineMode !== 'seller');
    const wantsRefi =
      user?.onboardingIntent === 'refinance' ||
      user?.onboardingIntent === 'rate_watch';
    const preferred = own.filter((p) => p.isRefinance === wantsRefi);
    return preferred[0] ?? own[0];
  }, [allProperties, user?.onboardingIntent]);

  const start = useCallback(
    async (property?: PropertyView) => {
      const target = property ?? defaultProperty;
      const isRefi = target
        ? target.isRefinance
        : user?.onboardingIntent === 'refinance';
      try {
        const app = await getOrCreate({
          flowProfile: isRefi ? 'refinance' : 'purchase',
          ...(target ? { focusProductId: target.productId } : {}),
          ...(isRefi && target ? { sourceProductId: target.productId } : {}),
          approvalChannel: 'start_application',
        }).unwrap();
        navigate(`/loan/${app._id}`);
      } catch {
        toast.error('Could not open your application. Please try again.');
      }
    },
    [defaultProperty, getOrCreate, navigate, user?.onboardingIntent]
  );

  return { start, isStarting: isLoading, defaultProperty };
}
